import React, { useState, useEffect } from 'react';
import MaterialTable from 'material-table';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Chip,
} from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import Swal from 'sweetalert2';
import request from '../../../services/Request';

const BASE = '/external/admin';

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request({ method: 'get', url: `${BASE}/apikeys` }).then((res) => {
      if (!cancelled) {
        setKeys(res.data);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [refresh]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await request({ method: 'post', url: `${BASE}/apikeys`, data: { name: newName } });
      setGeneratedKey(res.data.key);
      setNewName('');
      refresh ? setRefresh(false) : setRefresh(true);
    } catch (e) {
      Swal.fire({ icon: 'error', text: 'Failed to create API key' });
    }
  };

  const handleDelete = async (id, name) => {
    const confirm = await Swal.fire({
      title: `Delete "${name}"?`,
      text: 'This key will stop working immediately.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
    });
    if (!confirm.isConfirmed) return;
    await request({ method: 'delete', url: `${BASE}/apikeys/${id}` });
    Swal.fire({ icon: 'success', text: 'API key deleted', timer: 1200, showConfirmButton: false });
    refresh ? setRefresh(false) : setRefresh(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    Swal.fire({ icon: 'success', text: 'Copied to clipboard', timer: 1200, showConfirmButton: false });
  };

  const columns = [
    { title: 'Name', field: 'name' },
    { title: 'Key', field: 'key', render: (row) => (
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {row.key?.slice(0, 20)}...
        </Typography>
        <FileCopyOutlinedIcon
          style={{ cursor: 'pointer', fontSize: 16, color: '#666' }}
          onClick={() => copyToClipboard(row.key)}
        />
      </Box>
    )},
    { title: 'Created', field: 'createdAt', render: (row) => new Date(row.createdAt).toLocaleDateString() },
  ];

  return (
    <div>
      <MaterialTable
        isLoading={loading}
        columns={columns}
        data={keys}
        title="API Keys"
        actions={[
          {
            icon: 'add',
            tooltip: 'Create API Key',
            isFreeAction: true,
            onClick: () => setCreateOpen(true),
          },
          {
            icon: 'delete',
            tooltip: 'Delete Key',
            onClick: (e, row) => handleDelete(row._id, row.name),
          },
        ]}
        options={{ actionsColumnIndex: -1, pageSize: 10, search: false }}
      />

      {/* Create Key Dialog */}
      <Dialog open={createOpen && !generatedKey} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="Key Name (e.g. TCG Production)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth
            autoFocus
            variant="outlined"
            style={{ marginTop: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} color="primary" variant="contained" disabled={!newName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show Generated Key Dialog */}
      <Dialog open={!!generatedKey} maxWidth="sm" fullWidth>
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Copy this key now — it will not be shown again.
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            style={{ background: '#f5f5f5', padding: '12px 16px', borderRadius: 4, marginTop: 8, gap: 12 }}
          >
            <Typography variant="body2" style={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
              {generatedKey}
            </Typography>
            <FileCopyOutlinedIcon
              style={{ cursor: 'pointer', color: '#1976d2', flexShrink: 0 }}
              onClick={() => copyToClipboard(generatedKey)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setGeneratedKey(''); setCreateOpen(false); }}
            color="primary"
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
